import { Grid, InputBaseComponentProps } from '@mui/material';
import { CustomFieldsMapper, Location, LocationMapper } from '@nx-smartmonkey/shared/domain';
import {
  dateFormatShortTime,
  formatPhoneNumber,
  parseSecondsToHM,
  validatePhoneNumber,
} from '@nx-smartmonkey/shared/helpers';
import {
  Checkbox,
  RoutalInputPhone,
  RoutalInputPhoneValue,
  RoutalPalette,
  RoutalTooltip,
  VSpacer,
  getCountryCallingCode,
} from '@nx-smartmonkey/ui';
import { CountryCode } from 'libphonenumber-js';
import { CSSProperties, useEffect, useMemo, useState } from 'react';

import WarningIcon from '../../assets/svg/WarningIcon';
import { FormArray } from '../../components/form-fields/FormArray';
import { FormLinkedElement } from '../../components/form-fields/FormLinkedElement';
import { FormPickup } from '../../components/form-fields/FormPickup';
import FormTimeWindows from '../../components/form-fields/FormTimeWindows';
import { Client } from '../../domain/client/Client';
import { Driver } from '../../domain/driver/Driver';
import { Stop } from '../../domain/stop/Stop';
import { intl } from '../../helpers/IntGlobalProvider';
import FormattedMessage from '../../react-components/FormattedCustomMessage';
import InputLocation from '../../react-components/InputLocation';
import InputText from '../../react-components/InputText';
import Text from '../../react-components/Text';
import { CreatePickupData } from '../../repositories/stop/StopRepository';

const dataTransform = (data: any, property: any) => {
  switch (property) {
    case `planned_arrival_time`:
    case `planned_departure_time`:
    case `planned_end_time`:
    case `planned_start_time`:
      return parseSecondsToHM(data);
    case `done_at`:
      return dateFormatShortTime(new Date(data));
    case `stops`:
      return data.length;
    default:
      return data;
  }
};

export const renderInfo = (prefix: any, property: any, data: any) => (
  <>
    <Text variant="label" intlId={`${prefix}.${property}`} style={{ cursor: `default`, marginBottom: `8px` }} />
    <Text
      style={{
        cursor: `default`,
        minHeight: `32px`,
        padding: `0px`,
        fontSize: `16px`,
        lineHeight: `normal`,
        color: RoutalPalette.text,
      }}
    >
      {dataTransform(data[property], property)}
    </Text>
  </>
);

export const renderCheckbox = (prefix: any, property: any, handleUpdate: any, data: any) => (
  <div>
    <Text intlId={`${prefix}.${property}`} style={{ cursor: `default`, marginBottom: `8px` }} />
    <Checkbox
      checked={dataTransform(data[property], property)}
      onChange={(e: any, value: any) => {
        handleUpdate(property, value);
      }}
    />
  </div>
);

interface RenderInputTextProps {
  data: any;
  property: any;
  handleUpdate: (...args: any[]) => any;
  labelIntlId?: string;
  placeholderIntlId?: string;
  type?: any;
  variant?: `flat` | `outlined` | `clean`;
  suffix?: string;
  multiline?: boolean;
  size?: `normal` | `title`;
  rowsMin?: number;
  rowsMax?: number;
  computed?: (value: any) => any;
}

interface RenderInputPhoneProps {
  data: any;
  property: any;
  handleUpdate: (...args: any[]) => any;
  labelIntlId?: string;
  placeholderIntlId?: string;
  defaultCountry?: string;
  disabled?: boolean;
}

export const RenderInputPhone = ({
  data,
  property,
  handleUpdate,
  labelIntlId,
  placeholderIntlId,
  defaultCountry = `es`,
  disabled = false,
}: RenderInputPhoneProps) => {
  const countryDialCode = useMemo(
    () => getCountryCallingCode(defaultCountry.toUpperCase() as CountryCode),
    [defaultCountry]
  );
  const [phone, setPhone] = useState<string>(
    validatePhoneNumber(data[property]) ? formatPhoneNumber({ phone: data[property], dialCode: countryDialCode }) : ``
  );
  const [dialCode, setDialCode] = useState<string>(countryDialCode);

  useEffect(() => {
    const newPhone = validatePhoneNumber(data[property]) ? formatPhoneNumber({ phone: data[property], dialCode }) : ``;
    if (phone !== newPhone) {
      setPhone(newPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, property]);

  return (
    <Grid container direction="row" wrap="nowrap" style={{ position: `relative` }}>
      <Grid item container direction="column" wrap="nowrap">
        {labelIntlId && <Text intlId={labelIntlId} variant="label" style={{ marginBottom: `8px` }} />}

        <RoutalInputPhone
          disabled={disabled}
          countryCallingCodeEditable={false}
          focusInputOnCountrySelection
          international
          placeholder={intl.formatMessage({ id: placeholderIntlId })}
          defaultCountry={defaultCountry}
          value={phone as RoutalInputPhoneValue}
          onCountryChange={(country: CountryCode | undefined) => {
            if (country) {
              const newDialCode = getCountryCallingCode(country);
              setDialCode(newDialCode);
            }
          }}
          onBlur={(event: React.FocusEvent<HTMLElement>) => {
            if (phone === ``) {
              handleUpdate(property, null);
            }
            if (validatePhoneNumber(phone)) {
              const newPhone = formatPhoneNumber({ phone, dialCode });
              const originalPhone = data[property] ?? ``;
              if (newPhone !== originalPhone) {
                handleUpdate(property, newPhone !== dialCode && newPhone !== `` ? newPhone : null);
              }
            }
          }}
          onChange={(value?: RoutalInputPhoneValue) => {
            setPhone(value ?? ``);
          }}
        />
      </Grid>

      {phone && formatPhoneNumber({ phone, dialCode }) !== `` ? (
        <>
          {!validatePhoneNumber(formatPhoneNumber({ phone, dialCode })) ? (
            <Grid
              item
              style={{
                right: `4px`,
                position: `absolute`,
                alignSelf: `flex-end`,
              }}
            >
              <RoutalTooltip
                title={<Text style={{ color: RoutalPalette.neutral00 }} intlId="invalid_phone_prefix" />}
                placement="left"
                overlayStyle={{
                  maxWidth: `300px`,
                }}
              >
                <WarningIcon style={{ color: RoutalPalette.warning.medium }} />
              </RoutalTooltip>
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

interface RenderInputTextProps {
  data: any;
  property: any;
  handleUpdate: (...args: any[]) => any;
  labelIntlId?: string;
  placeholder?: string;
  placeholderIntlId?: string;
  placeholderIntlValues?: Record<string, string | number>;
  type?: any;
  variant?: `flat` | `outlined` | `clean`;
  suffix?: string;
  multiline?: boolean;
  size?: `normal` | `title`;
  rowsMin?: number;
  rowsMax?: number;
  inputContainerStyles?: CSSProperties;
  inputStyles?: CSSProperties;
  inputProps?: InputBaseComponentProps;
  computed?: (value: any) => any;
}

export const renderInputText = ({
  data,
  property,
  handleUpdate,
  labelIntlId,
  placeholder,
  placeholderIntlId,
  placeholderIntlValues,
  type = `text`,
  variant = `clean`,
  suffix,
  size = `normal`,
  multiline,
  rowsMin = 2,
  rowsMax = 2,
  inputContainerStyles,
  inputStyles,
  inputProps,
  computed = (value: any) => value,
}: RenderInputTextProps) => (
  <>
    {labelIntlId && <Text intlId={labelIntlId} variant="label" style={{ marginBottom: `8px` }} />}
    <InputText
      inputContainerStyles={{
        width: `100%`,
        borderRadius: `5px`,
        fontWeight: size === `title` ? 600 : undefined,
        ...inputContainerStyles,
      }}
      inputStyles={{
        color: RoutalPalette.text,
        fontSize: size === `title` ? `16px` : `13px`,
        ...inputStyles,
      }}
      inputProps={inputProps}
      variant={variant}
      type={type}
      fieldValue={computed(data[property])}
      handleEnter={(val: any) => {
        handleUpdate(property, val);
      }}
      bGColor="transparent"
      placeholder={placeholder}
      placeholderIntlValues={placeholderIntlValues}
      placeholderIntlId={placeholderIntlId}
      suffix={suffix}
      multiline={multiline}
      rowsMin={rowsMin}
      rowsMax={rowsMax}
    />
  </>
);

interface RenderLinkedElementProps {
  prefix: string;
  property: string;
  handleUpdate: (label: string, val: any) => void;
  data: Driver | Stop;
  type: `client` | `vehicle`;
  isClearable: boolean;
}

const RenderLinkedElement = ({ prefix, property, handleUpdate, data, type, isClearable }: RenderLinkedElementProps) => (
  <FormLinkedElement
    type={type}
    isClearable={isClearable}
    projectId={data.project_id}
    fieldValue={(data as Record<string, any>)[property]}
    onChange={(val: any) => {
      if (val === null) {
        handleUpdate(property, null);
      } else {
        handleUpdate(property, val.id);
      }
    }}
    noOptionsMessage={<Text intlId={`${prefix}.${property}.no_options`} />}
    placeholder={
      <Text
        intlId={`${prefix}.${property}.placeholder`}
        style={{ color: RoutalPalette.neutral20, cursor: `pointer` }}
      />
    }
  />
);

interface RenderLinkedClientProps {
  prefix: string;
  property: string;
  handleUpdate: (label: string, val: any) => void;
  stop: Stop;
}

export const renderLinkedClient = ({ prefix, property, handleUpdate, stop }: RenderLinkedClientProps) => (
  <RenderLinkedElement
    prefix={prefix}
    property={property}
    handleUpdate={handleUpdate}
    data={stop}
    type="client"
    isClearable
  />
);

interface RenderLinkedVehicleProps {
  prefix: string;
  property: string;
  handleUpdate: (label: string, val: any) => void;
  driver: Driver;
}

export const renderLinkedVehicle = ({ prefix, property, handleUpdate, driver }: RenderLinkedVehicleProps) => (
  <RenderLinkedElement
    prefix={prefix}
    property={property}
    handleUpdate={handleUpdate}
    data={driver}
    type="vehicle"
    isClearable
  />
);

export const renderPickup = ({
  prefix,
  handleDeletePickup,
  handleCreatePickup,
  pickup,
}: {
  prefix: string;
  handleDeletePickup: () => void;
  handleCreatePickup: ({ pickupData }: { pickupData: CreatePickupData }) => void;
  pickup?: Stop;
}) => (
  <FormPickup
    label={<FormattedMessage id={`${prefix}.pickup`} />}
    fieldValue={pickup}
    onChange={(val: any) => {
      if (val === null) {
        handleDeletePickup();
      } else if (!val.id) {
        handleCreatePickup({ pickupData: { label: val.label } });
      } else {
        const {
          id,
          label,
          location,
          location_details,
          external_id: extId,
          reference_person,
          phone,
          email,
          url,
          default_duration: duration,
          default_max_delivery_time: max_delivery_time,
          default_requires: requires,
          default_cluster: cluster,
          default_time_windows: time_windows,
          custom_fields,
          // TODO: custom_fields
        } = val as Client;

        handleCreatePickup({
          pickupData: {
            client_id: id,
            label: label as string,
            location: location ? LocationMapper.toDTO(location) : undefined,
            location_details,
            external_id: extId,
            reference_person,
            phone,
            email,
            url,
            duration,
            max_delivery_time,
            requires,
            cluster,
            time_windows,
            custom_fields: custom_fields && CustomFieldsMapper.toRepository(custom_fields),
          },
        });
      }
    }}
    placeholder={<Text intlId={`${prefix}.pickup.placeholder`} />}
  />
);

export const renderTimewindows = (prefix: any, property: any, handleUpdate: any, data: any, single = false) => (
  <FormTimeWindows
    label={<FormattedMessage id={`${prefix}.${property}`} />}
    timeWindows={data[property]}
    onTimewindowsChange={(val: any) => {
      handleUpdate(property, val);
    }}
    single={single}
  />
);

interface RenderInputLocationProps {
  prefix: string;
  property: string;
  handleUpdate: (property: string, location?: Location | null) => void;
  data: any;
}

export const renderInputLocation = ({ prefix, property, handleUpdate, data }: RenderInputLocationProps) => (
  <InputLocation
    fullWidth
    clearable
    inputProps={{
      styles: {},
      size: `medium`,
      variant: `outlined`,
      placeholderIntlId: `${prefix}.${property}`,
    }}
    emptyIntlId="no_results"
    fieldValue={data[property]}
    onUpdate={(location?: Location | null) => {
      handleUpdate(property, location);
    }}
  />
);

interface InputLocationsProps {
  prefix: string;
  propertyStart: string;
  propertyEnd: string;
  data: any;
  handleUpdate: (data: Record<string, any>) => any;
}

export const InputLocations = ({ prefix, propertyStart, propertyEnd, data, handleUpdate }: InputLocationsProps) => {
  const [isCircular, setIsCircular] = useState<boolean>(false);

  useEffect(() => {
    if (!data[propertyStart] && !data[propertyEnd]) {
      setIsCircular(true);
      return;
    }
    if (data[propertyStart] && data[propertyEnd]) {
      const areEqual =
        data[propertyStart].lat === data[propertyEnd].lat && data[propertyStart].lng === data[propertyEnd].lng;
      setIsCircular(areEqual);
    } else {
      setIsCircular(false);
    }
  }, [data, propertyEnd, propertyStart]);

  return (
    <>
      <Grid container direction="row" alignItems="center" style={{ marginBottom: `8px` }}>
        <Text intlId={`${prefix}.${propertyStart}`} variant="label" />
        <Text style={{ margin: `0 4px` }} variant="label">
          /
        </Text>
        <Text intlId={`${prefix}.location_is_circular`} variant="label" style={{ marginRight: `4px` }} />
        <Checkbox
          disabled={!data[propertyStart]}
          checked={isCircular}
          onChange={(e: any) => {
            if (!data[propertyStart] && !data[propertyEnd]) {
              return;
            }

            if (!e.target.checked) {
              handleUpdate({ [propertyEnd]: null });
              setIsCircular(false);
              return;
            }

            if (data[propertyStart]) {
              handleUpdate({ [propertyEnd]: data[propertyStart] });
              setIsCircular(true);
              return;
            }

            if (data[propertyEnd]) {
              setIsCircular(true);
              handleUpdate({ [propertyStart]: data[propertyEnd] });
            }
          }}
        />
      </Grid>

      <InputLocation
        fullWidth
        clearable
        inputProps={{
          styles: {},
          size: `medium`,
          variant: `outlined`,
          placeholderIntlId: `${prefix}.${propertyStart}`,
        }}
        emptyIntlId="no_results"
        fieldValue={data[propertyStart]}
        onUpdate={(val: any) => {
          handleUpdate({
            [propertyStart]: val,
            ...(isCircular
              ? {
                  [propertyEnd]: val,
                }
              : {}),
          });
        }}
      />

      {!isCircular ? (
        <>
          <VSpacer small />
          <InputLocation
            fullWidth
            clearable
            inputProps={{
              styles: {},
              size: `medium`,
              variant: `outlined`,
              placeholderIntlId: `${prefix}.${propertyEnd}`,
            }}
            emptyIntlId="no_results"
            fieldValue={data[propertyEnd]}
            onUpdate={(val: any) => handleUpdate({ [propertyEnd]: val })}
          />
        </>
      ) : null}
    </>
  );
};

export const renderArray = (prefix: any, property: any, handleUpdate: any, data: any) => (
  <FormArray
    label={<Text variant="label" intlId={`${prefix}.${property}`} />}
    array={data[property]}
    onUpdate={(chips: any) => handleUpdate(property, chips)}
  />
);
